// Site-wide content used across home + subvertical pages.
import {
  Inbox,
  ClipboardList,
  Banknote,
  RotateCw,
  HandCoins,
  Magnet,
} from "lucide-react";

export const TRADES = [
  { slug: "roofing",     label: "Roofing" },
  { slug: "solar",       label: "Solar" },
  { slug: "pest",        label: "Pest Control" },
  { slug: "landscape",   label: "Landscaping" },
  { slug: "pools",       label: "Pools" },
  { slug: "foundation",  label: "Foundation" },
];

export const INTEGRATIONS_ALL = [
  "AccuLynx", "JobNimbus", "FieldRoutes", "GorillaDesk",
  "Aspire", "LMN", "Aurora Solar", "QuickBooks",
  "CompanyCam", "RoofSnap", "ServiceTitan", "Housecall Pro",
];

export const WORKFLOWS = [
  {
    id: "inbound-lead",
    title: "Inbound Lead Detection",
    icon: Magnet,
    summary: "New lead lands in your CRM. We see it inside 60 seconds and draft the first reply.",
    sample: {
      to: "mike@oakridge-construction.com",
      subject: "Re: New estimate request — 2841 Birch Hill",
      body:
        "Hi Mike — thanks for reaching out about the roof. I can be at the property Thursday morning around 8am for a 20-minute look. If that works, hit reply with the gate code and I'll lock it in. — Tom",
    },
  },
  {
    id: "estimate-followup",
    title: "Estimate Follow-Up",
    icon: ClipboardList,
    summary: "Quote went out 7 days ago and went quiet. We write the nudge that doesn't sound like a nudge.",
    sample: {
      to: "homeowner@gmail.com",
      subject: "Quick one on the Birch Hill quote",
      body:
        "Hey — just checking in on the quote we sent last Tuesday. No pressure either way. If you've got questions on the cricket detail or the underlayment line, happy to walk it. — Tom",
    },
  },
  {
    id: "ar-aging",
    title: "AR Aging Brief",
    icon: Banknote,
    summary: "Invoice hits 30 days. We draft the polite reminder you'd write if you had ten more minutes.",
    sample: {
      to: "ap@birchhill-properties.com",
      subject: "Invoice #4192 — friendly reminder",
      body:
        "Hi — just a heads up that invoice 4192 hit 30 days yesterday. If it's already on the next check run, ignore me. Otherwise, here's the link to pay online. Thanks. — Tom",
    },
  },
  {
    id: "renewal",
    title: "Renewal Tracking",
    icon: RotateCw,
    summary: "Service contract is 30 days from rolling. We catch it and draft the renewal note.",
    sample: {
      to: "ops@birchhill-properties.com",
      subject: "Your roof maintenance plan rolls Aug 14",
      body:
        "Quick note — your annual maintenance plan auto-renews August 14. Same crew, same price, no action needed unless you want to upgrade to the storm-add-on. Drop a reply if you do. — Tom",
    },
  },
  {
    id: "referral",
    title: "Referral Capture",
    icon: HandCoins,
    summary: "Job finishes 5-star. We draft the referral ask while you're still in the driveway.",
    sample: {
      to: "happy.customer@gmail.com",
      subject: "Glad we got that one buttoned up",
      body:
        "Thanks for the kind note today, Sara. If anyone on your street is dealing with the same kind of storm damage, we'd appreciate the intro. No pressure — just a quick reply with a name works. — Tom",
    },
  },
];

export const PRICING_TIERED = [
  {
    name: "Starter",
    price: "$199",
    cadence: "/mo",
    desc: "Solo operators or 1–2 truck shops.",
    bullets: [
      "1 workflow",
      "1 software integration",
      "Daily Morning Brief",
      "Email-only delivery",
    ],
  },
  {
    name: "Operator",
    price: "$499",
    cadence: "/mo",
    desc: "The shop that's outgrowing the office manager.",
    bullets: [
      "3 workflows",
      "3 integrations",
      "Morning + afternoon brief",
      "Slack or SMS delivery",
    ],
    highlight: true,
  },
  {
    name: "Studio",
    price: "$999",
    cadence: "/mo",
    desc: "Multi-crew shops with real volume.",
    bullets: [
      "All 6 workflows",
      "Unlimited integrations",
      "Custom recipes",
      "Priority queue",
    ],
  },
  {
    name: "Enterprise",
    price: "$2,499–$4,999",
    cadence: "/mo",
    desc: "Regional rollups, multi-location ops.",
    bullets: [
      "Everything in Studio",
      "Dedicated agent team",
      "SLAs, custom recipes",
      "Direct line to product",
    ],
  },
];

export const PRICING_OUTCOME = [
  {
    name: "Lost Lead Recovery",
    price: "$399",
    or: "or 10% of recovered revenue",
    desc: "We only get paid when a dead lead becomes signed paper.",
  },
  {
    name: "Speed-to-Lead",
    price: "$299",
    or: "or $5 per lead",
    desc: "Every inbound lead replied to in under 5 minutes. Or it's free.",
  },
  {
    name: "Renewal Recovery",
    price: "$349",
    or: "or 5% of renewals",
    desc: "Catch every contract before it rolls. Pay only on saves.",
  },
];

export const FAQS = [
  {
    q: "Do you text or call my customers?",
    a: "No. Never. We draft replies inside the software you already use. You read them. You hit send. The customer only ever hears from you. That restriction is the product.",
  },
  {
    q: "What software do you integrate with?",
    a: "AccuLynx, JobNimbus, FieldRoutes, GorillaDesk, Aspire, LMN, Aurora Solar, QuickBooks, CompanyCam, RoofSnap, ServiceTitan, Housecall Pro. If you run something else, ask — we add one new connector every two weeks.",
  },
  {
    q: "What happens if I cancel?",
    a: "You stop paying. We stop watching. Your data is yours — we keep nothing past 30 days. There is no exit interview, no winback sequence, no friction. We earn the next month or we don't.",
  },
  {
    q: "How do you protect my customer data?",
    a: "Read-only access to your CRM. SOC 2 in progress. Data lives in encrypted Postgres in US-East. We never sell it, never share it, never use it to train anything. You can request a full export and a hard delete from one email.",
  },
  {
    q: "Is this just ChatGPT in a wrapper?",
    a: "No. The drafts are written by a tuned agent that reads your CRM, your job notes, your past correspondence, and your voice — not a model picking words out of the air. The wrapper, as you call it, is the whole job.",
  },
];

export const SUBVERTICALS = {
  roofing: {
    label: "Roofing",
    hero_h1: "Never miss another roof.",
    hero_sub:
      "LeadRiver watches AccuLynx, JobNimbus, and CompanyCam, then drafts your follow-ups before you climb down.",
    stats: [
      { value: "65%", label: "of storm leads go unanswered the first hour" },
      { value: "5 min", label: "the window before a hot roofing lead goes cold" },
      { value: "$11,400", label: "average value of one missed re-roof" },
    ],
    workflows: ["inbound-lead", "estimate-followup", "referral"],
    integrations: ["AccuLynx", "JobNimbus", "CompanyCam", "RoofSnap", "QuickBooks"],
    image: "/hero-leadriver.jpg",
    quote: {
      body: "We were leaking three roofs a week — leads, not water. Now I open one email at 6am and the day is sorted.",
      who: "— Tom, owner, two-truck roofing shop, Texas",
    },
  },
  solar: {
    label: "Solar",
    hero_h1: "Never miss another panel deal.",
    hero_sub:
      "LeadRiver watches Aurora and your CRM, drafts the proposal follow-up, and pings you when the contract goes quiet.",
    stats: [
      { value: "72%", label: "of solar leads close to whoever calls first" },
      { value: "4 min", label: "median attention span on a financing email" },
      { value: "$28,900", label: "average residential solar contract" },
    ],
    workflows: ["inbound-lead", "estimate-followup", "referral"],
    integrations: ["Aurora Solar", "JobNimbus", "QuickBooks", "CompanyCam"],
    image: "/hero-solar.jpg",
    quote: {
      body: "Atlas wrote a better proposal follow-up than I would have. I just changed two words and hit send.",
      who: "— Dani, solar installer, Arizona",
    },
  },
  pest: {
    label: "Pest Control",
    hero_h1: "Never miss another route renewal.",
    hero_sub:
      "LeadRiver watches FieldRoutes and GorillaDesk, then drafts your renewal letters and AR nudges before the route goes out.",
    stats: [
      { value: "31%", label: "of quarterlies churn from missed renewal touches" },
      { value: "48 hr", label: "the window before a cancellation hardens" },
      { value: "$640", label: "annual value of one residential quarterly" },
    ],
    workflows: ["renewal", "ar-aging", "referral"],
    integrations: ["FieldRoutes", "GorillaDesk", "QuickBooks"],
    image: "https://images.unsplash.com/photo-1581578017093-cd30fce4eeb7?auto=format&fit=crop&w=1400&q=80",
    quote: {
      body: "We saved 19 renewals in the first 30 days. That's more than the subscription costs for a year.",
      who: "— Carlos, pest control operator, Florida",
    },
  },
  landscape: {
    label: "Landscaping",
    hero_h1: "Never miss another spring contract.",
    hero_sub:
      "LeadRiver watches Aspire and LMN, drafts your seasonal renewals, and surfaces the bids that are going cold.",
    stats: [
      { value: "58%", label: "of last-year's clients never get a renewal touch" },
      { value: "9 days", label: "average time a bid sits without follow-up" },
      { value: "$4,200", label: "average residential seasonal contract" },
    ],
    workflows: ["renewal", "estimate-followup", "referral"],
    integrations: ["Aspire", "LMN", "QuickBooks"],
    image: "/hero-landscape.jpg",
    quote: {
      body: "I stopped chasing renewals in the truck. The brief lands at 6, I send by 6:10.",
      who: "— Pat, landscape contractor, Ohio",
    },
  },
  pools: {
    label: "Pools",
    hero_h1: "Never miss another opening.",
    hero_sub:
      "LeadRiver catches pre-season opening requests and seasonal close-out renewals while you're on the deck.",
    stats: [
      { value: "44%", label: "of openings are booked in a 3-week window" },
      { value: "11 days", label: "the gap most shops have between request and reply" },
      { value: "$1,850", label: "average opening + chemical kit" },
    ],
    workflows: ["inbound-lead", "renewal", "ar-aging"],
    integrations: ["ServiceTitan", "Housecall Pro", "QuickBooks"],
    image: "/hero-pools.jpg",
    quote: {
      body: "We booked an extra eighteen openings this April. I didn't make a single extra call.",
      who: "— Jen, pool service owner, Florida",
    },
  },
  foundation: {
    label: "Foundation",
    hero_h1: "Never miss another estimate.",
    hero_sub:
      "LeadRiver watches your CRM and CompanyCam, then drafts the follow-up the day the inspector's report comes back.",
    stats: [
      { value: "23 days", label: "average lag between estimate and decision" },
      { value: "3 of 5", label: "foundation jobs lost to silence, not price" },
      { value: "$18,500", label: "average residential pier-and-beam project" },
    ],
    workflows: ["estimate-followup", "renewal", "ar-aging"],
    integrations: ["JobNimbus", "CompanyCam", "QuickBooks"],
    image: "/hero-foundation.jpg",
    quote: {
      body: "Foundation work is slow to close. The follow-ups Atlas drafted got three deals over the line.",
      who: "— Ray, foundation repair contractor, Texas",
    },
  },
};

export const HERO_IMAGE = "/hero-leadriver.jpg";
export const HANDS_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/fb946d65-0df7-432f-9a18-dcf9fb0025ad/images/10aeddef4309af5e2125f7805a7594d6197c0974c8bd09844586c6fd147ded73.png";
export const LAPTOP_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/fb946d65-0df7-432f-9a18-dcf9fb0025ad/images/ed3682dd3548d3a088873b56a3a6ad2f42afb8df354db1d94aaad1cd9bad5896.png";
